import { useEffect, useState, type ChangeEvent } from 'react';
import { Eye, ChevronLeft, ChevronRight, Check, Loader2 } from 'lucide-react';
import { ApiError, downloadUrl } from '@/lib/apiClient';
import {
  useCreateCategory,
  useDeleteCategory,
  useDeleteLibraryFile,
  useLibraryCategories,
  useLibraryFiles,
  useUploadLibraryFile,
} from '@/queries/library.queries';
import { Button, Input, cx } from '@/components/ui';
import { Icon } from '@/components/ui/icons';
import { FilePreviewModal } from '@/components/FilePreviewModal';
import type { LibraryFile } from '@/lib/types';

export function LibraryView({ canManage }: { canManage: boolean }) {
  const [selected, setSelected] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [newCat, setNewCat] = useState('');
  const [preview, setPreview] = useState<LibraryFile | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [deletingCat, setDeletingCat] = useState<{ id: string; name: string } | null>(null);
  const [deletingFile, setDeletingFile] = useState<{ id: string; title: string } | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [tempUploadingFile, setTempUploadingFile] = useState<{ filename: string; byte_size: number } | null>(null);
  const [showSuccessPopup, setShowSuccessPopup] = useState<string | null>(null);

  function fail(e: unknown) {
    setError(e instanceof ApiError ? e.message : 'Something went wrong');
  }

  const categoriesQuery = useLibraryCategories();
  const categories = categoriesQuery.data ?? [];

  // Keep a valid selection as categories load/change.
  useEffect(() => {
    if (categories.length === 0) return;
    setSelected((cur) => (cur && categories.some((c) => c.id === cur) ? cur : categories[0].id));
  }, [categories]);

  useEffect(() => {
    setCurrentPage(1);
  }, [selected, itemsPerPage, searchQuery]);

  const filesQuery = useLibraryFiles({
    categoryId: selected ?? undefined,
    search: searchQuery,
    page: currentPage,
    limit: itemsPerPage,
  });

  const filesData = filesQuery.data;
  const files = filesData?.items ?? [];
  const totalItems = filesData?.total ?? 0;
  const totalPages = filesData?.totalPages ?? 1;
  const activePage = filesData?.page ?? currentPage;

  const startItem = totalItems === 0 ? 0 : (activePage - 1) * itemsPerPage + 1;
  const endItem = Math.min(activePage * itemsPerPage, totalItems);

  const createCategory = useCreateCategory();
  const deleteCategory = useDeleteCategory();
  const uploadFile = useUploadLibraryFile();
  const deleteFile = useDeleteLibraryFile();

  const uploading = uploadFile.isPending;

  function addCategory() {
    if (!newCat.trim()) return;
    setError(null);
    createCategory.mutate(newCat.trim(), {
      onSuccess: (c) => {
        setNewCat('');
        setSelected(c.id);
      },
      onError: fail,
    });
  }

  function removeCategory(id: string) {
    deleteCategory.mutate(id, {
      onSuccess: () => {
        setSelected((cur) => (cur === id ? (categories.find((c) => c.id !== id)?.id ?? null) : cur));
        setDeletingCat(null);
      },
      onError: (e) => {
        fail(e);
        setDeletingCat(null);
      },
    });
  }

  function onUpload(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file || !selected) return;
    setError(null);
    setTempUploadingFile({ filename: file.name, byte_size: file.size });
    uploadFile.mutate(
      { categoryId: selected, title: file.name, file },
      {
        onSuccess: () => {
          setCurrentPage(1);
          setShowSuccessPopup(file.name);
        },
        onError: fail,
        onSettled: () => {
          setTempUploadingFile(null);
          e.target.value = '';
        },
      },
    );
  }

  function removeFile(id: string) {
    deleteFile.mutate(id, {
      onSuccess: () => {
        setDeletingFile(null);
      },
      onError: (e) => {
        fail(e);
        setDeletingFile(null);
      },
    });
  }

  if (categoriesQuery.isError && !error) fail(categoriesQuery.error);

  return (
    <div className="flex flex-col gap-6">
      {error && (
        <p role="alert" className="text-sm text-destructive">
          {error}
        </p>
      )}

      <div className="grid md:grid-cols-[220px_1fr] gap-5 items-start">
        {/* categories */}
        <aside className="card p-2 flex flex-col gap-1">
          {categories.map((c) => (
            <div key={c.id} className="group flex items-center gap-1">
              <button
                onClick={() => setSelected(c.id)}
                className={cx(
                  'flex-1 text-left h-9 px-3 rounded-lg text-sm font-medium transition-colors',
                  selected === c.id
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-accent',
                )}
              >
                {c.name}
              </button>
              {canManage && (
                <button
                  onClick={() => setDeletingCat({ id: c.id, name: c.name })}
                  title="Delete category"
                  className="opacity-10 group-hover:opacity-100 w-8 h-8 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-all font-semibold flex items-center justify-center shrink-0"
                >
                  x
                </button>
              )}
            </div>
          ))}
          {canManage && (
            <div className="flex items-center gap-1.5 mt-1 p-1">
              <Input
                value={newCat}
                onChange={(e) => setNewCat(e.target.value)}
                placeholder="New category"
                className="!h-9 text-sm"
              />
              <Button size="sm" onClick={addCategory} disabled={!newCat.trim()} className="!h-9">
                Add
              </Button>
            </div>
          )}
        </aside>

        {/* files */}
        <div className="flex flex-col gap-3">
          {selected && (
            <div className="flex flex-wrap gap-2 card p-3">
              <div className="relative flex-1 min-w-[180px] flex items-center">
                <span className="absolute left-3.5 flex items-center pointer-events-none text-muted-foreground">
                  <Icon.search width={16} height={16} />
                </span>
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search files in this category..."
                  className="pl-10 pr-16 h-10 text-sm"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    className="absolute right-3.5 text-xs font-semibold text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Clear
                  </button>
                )}
              </div>
              {canManage && (
                <label className="inline-flex items-center gap-2 h-10 px-4 rounded-xl bg-primary text-primary-foreground font-medium text-sm cursor-pointer hover:brightness-110 transition shrink-0">
                  <Icon.upload width={16} height={16} />
                  {uploading ? 'Uploading…' : 'Upload file'}
                  <input
                    type="file"
                    accept=".docx,.pdf,.pptx,.xlsx,.txt,.md,.png,.jpg,.jpeg"
                    onChange={onUpload}
                    disabled={uploading}
                    hidden
                  />
                </label>
              )}
            </div>
          )}

          {filesQuery.isPending ? (
            <div className="card p-10 text-center text-muted-foreground">
              Loading files…
            </div>
          ) : (totalItems === 0 && !tempUploadingFile) ? (
            <div className="card p-10 text-center text-muted-foreground">
              {searchQuery ? 'No files match your search.' : 'No files in this category yet.'}
            </div>
          ) : (
            <div className="flex flex-col gap-4">
              <ul className="flex flex-col gap-2">
                {tempUploadingFile && (
                  <li className="card p-3.5 flex items-center gap-3 border border-primary/20 opacity-70 animate-pulse select-none">
                    <span className="grid place-items-center w-9 h-9 rounded-lg bg-muted border border-border text-primary shrink-0">
                      <Loader2 className="w-4 h-4 animate-spin" />
                    </span>
                    <div className="min-w-0 flex-1 text-left">
                      <div className="flex items-center gap-2">
                        <span className="block text-foreground font-medium truncate">{tempUploadingFile.filename}</span>
                        <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-primary/10 text-primary border border-primary/20 shrink-0">
                          Uploading...
                        </span>
                      </div>
                      <span className="block text-xs text-muted-foreground truncate font-mono">
                        {tempUploadingFile.filename} · {Math.max(1, Math.round(tempUploadingFile.byte_size / 1024))} KB
                      </span>
                    </div>
                  </li>
                )}
                {files.map((f) => (
                  <li
                    key={f.id}
                    className="group card p-3.5 flex items-center gap-3 hover:border-primary/40 transition-colors"
                  >
                    <span className="grid place-items-center w-9 h-9 rounded-lg bg-muted border border-border text-muted-foreground shrink-0">
                      <Icon.doc width={16} height={16} />
                    </span>
                    <button
                      onClick={() => setPreview(f)}
                      className="min-w-0 flex-1 text-left"
                      title="Preview"
                    >
                      <div className="flex items-center gap-2">
                        <span className="block text-foreground font-medium truncate">{f.title}</span>
                        {f.has_empty_sections && (
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-500/10 text-amber-600 dark:text-amber-400 border border-amber-500/20 shrink-0">
                            Needs Review
                          </span>
                        )}
                      </div>
                      <span className="block text-xs text-muted-foreground truncate font-mono">
                        {f.filename} · {Math.max(1, Math.round(f.byte_size / 1024))} KB
                      </span>
                    </button>
                    <button
                      onClick={() => setPreview(f)}
                      className="grid place-items-center w-9 h-9 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                      title="Preview"
                    >
                      <Eye className="h-4 w-4" />
                    </button>
                    <a
                      href={downloadUrl(`/library/files/${f.id}/download`)}
                      download
                      className="grid place-items-center w-9 h-9 rounded-lg text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                      title="Download"
                    >
                      <Icon.download width={16} height={16} />
                    </a>
                    {canManage && (
                      <button
                        onClick={() => setDeletingFile({ id: f.id, title: f.title })}
                        title="Delete file"
                        className="w-8 h-8 rounded-lg text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-all font-semibold flex items-center justify-center shrink-0"
                      >
                        x
                      </button>
                    )}
                  </li>
                ))}
              </ul>

              {totalItems > 0 && (
                <div className="flex items-center justify-between mt-4 px-1 gap-4 flex-wrap border-t border-border/40 pt-4">
                  <div className="text-xs text-muted-foreground select-none font-mono">
                    Showing <span className="text-primary font-semibold">{startItem}</span> – <span className="text-primary font-semibold">{endItem}</span> of <span className="text-primary font-semibold">{totalItems}</span>
                  </div>

                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-1.5">
                      <span className="text-xs text-muted-foreground select-none">Rows per page:</span>
                      <select
                        value={itemsPerPage}
                        onChange={(e) => setItemsPerPage(Number(e.target.value))}
                        className="bg-input border border-border text-foreground text-[11px] rounded-md px-2 py-1 outline-none cursor-pointer focus:border-primary transition font-semibold"
                      >
                        <option value={5}>5</option>
                        <option value={10}>10</option>
                        <option value={20}>20</option>
                        <option value={50}>50</option>
                      </select>
                    </div>

                    <div className="flex items-center gap-1">
                      {activePage > 1 && (
                        <Button
                          variant="subtle"
                          size="sm"
                          className="w-8 h-8 !p-0"
                          onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                          title="Previous Page"
                        >
                          <ChevronLeft className="h-4 w-4" />
                        </Button>
                      )}
                      {activePage < totalPages && (
                        <Button
                          variant="subtle"
                          size="sm"
                          className="w-8 h-8 !p-0"
                          onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                          title="Next Page"
                        >
                          <ChevronRight className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {preview && <FilePreviewModal file={preview} onClose={() => setPreview(null)} />}

      {deletingCat && (
        <DeleteCategoryModal
          categoryName={deletingCat.name}
          onConfirm={() => removeCategory(deletingCat.id)}
          onClose={() => setDeletingCat(null)}
        />
      )}

      {deletingFile && (
        <DeleteFileModal
          fileName={deletingFile.title}
          onConfirm={() => removeFile(deletingFile.id)}
          onClose={() => setDeletingFile(null)}
        />
      )}

      {showSuccessPopup && (
        <div className="fixed inset-0 flex items-center justify-center bg-black/60 z-[9999] backdrop-blur-sm overflow-hidden select-none">
          <div className="bg-card border border-border p-6 rounded-xl max-w-sm w-full mx-4 shadow-2xl flex flex-col items-center gap-4 text-center animate-rise">
            <div className="w-12 h-12 rounded-full bg-success/15 text-success flex items-center justify-center animate-pulse">
              <Check className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-foreground font-semibold text-lg">Upload Complete</h3>
              <p className="text-muted-foreground text-sm mt-1">
                <span className="font-mono text-foreground font-semibold break-all">{showSuccessPopup}</span> has been uploaded and processed successfully.
              </p>
            </div>
            <Button 
              onClick={() => setShowSuccessPopup(null)} 
              className="w-full mt-2"
            >
              Dismiss
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function DeleteFileModal({
  fileName,
  onConfirm,
  onClose,
}: {
  fileName: string;
  onConfirm: () => void;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md rounded-2xl border border-border bg-card p-6 shadow-2xl animate-in fade-in-50 zoom-in-95 duration-150">
        <h3 className="text-lg font-semibold text-foreground mb-2">Delete File</h3>
        <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
          Are you sure you want to permanently delete the file <span className="font-semibold text-foreground">"{fileName}"</span>? This action cannot be undone.
        </p>
        <div className="flex justify-end gap-3">
          <Button variant="subtle" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="danger" size="sm" onClick={onConfirm}>
            Delete
          </Button>
        </div>
      </div>
    </div>
  );
}

function DeleteCategoryModal({
  categoryName,
  onConfirm,
  onClose,
}: {
  categoryName: string;
  onConfirm: () => void;
  onClose: () => void;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md rounded-2xl border border-border bg-card p-6 shadow-2xl animate-in fade-in-50 zoom-in-95 duration-150">
        <h3 className="text-lg font-semibold text-foreground mb-2">Delete Category</h3>
        <p className="text-sm text-muted-foreground mb-6 leading-relaxed">
          Are you sure you want to delete the category <span className="font-semibold text-foreground">"{categoryName}"</span>? This will permanently delete the category and all files inside it. This action cannot be undone.
        </p>
        <div className="flex justify-end gap-3">
          <Button variant="subtle" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="danger" size="sm" onClick={onConfirm}>
            Delete
          </Button>
        </div>
      </div>
    </div>
  );
}
